import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import { xpForAnswer } from '../lib/practice.js';
import { playCorrect, playWrong } from '../lib/sfx.js';

/**
 * The practice runner.
 *
 * One component behind every question-and-options mode, because they differ
 * only in which questions they are handed and whether a clock is running.
 * Answers are reported upward as they happen, so XP and history survive
 * navigating away mid-session.
 *
 * `timeLimit` turns on the clock: seconds per question, a bonus for answering
 * quickly, and an automatic miss when it runs out. Running out is treated as a
 * wrong answer rather than a skip — otherwise the clock costs nothing and there
 * is no reason to hurry.
 */
export default function PracticeRunner({
  questions,
  lockedCount = 0,
  isPremium,
  soundOn = true,
  title = 'Quiz',
  blurb = null,
  timeLimit = null,
  onToggleSound,
  onAnswer,
  onFinish,
  onUpgrade,
  onExit,
}) {
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState([]);
  const [remaining, setRemaining] = useState(timeLimit);
  const [earned, setEarned] = useState(0);

  const deckKey = useMemo(() => questions.map((q) => q.id).join('|'), [questions]);
  useEffect(() => {
    setIndex(0);
    setSelected(null);
    setResults([]);
  }, [deckKey]);

  const question = questions[index];
  const answered = selected !== null;
  const finished = index >= questions.length;

  const choose = useCallback(
    (optionIndex) => {
      if (answered || !question) return;
      const correct = optionIndex === question.answer;
      const xp = xpForAnswer(question, { secondsLeft: remaining, limit: timeLimit });

      setSelected(optionIndex);
      setResults((prev) => [...prev, { id: question.id, correct }]);
      if (correct) setEarned((n) => n + xp);
      // Fired straight from the click handler, so the AudioContext is created
      // inside a user gesture and autoplay policy never blocks it.
      if (correct) playCorrect();
      else playWrong();
      onAnswer?.(question, correct, xp);
    },
    [answered, question, onAnswer, remaining, timeLimit],
  );

  /**
   * Time up. Recorded as a miss with the correct answer revealed — the same
   * shape as a wrong answer, so the rest of the screen needs no special case.
   * -1 can never equal `question.answer`, which is what marks it wrong.
   */
  const timeOut = useCallback(() => {
    if (answered || !question) return;
    setSelected(-1);
    setResults((prev) => [...prev, { id: question.id, correct: false }]);
    playWrong();
    onAnswer?.(question, false, 0);
  }, [answered, question, onAnswer]);

  const next = useCallback(() => {
    setSelected(null);
    setRemaining(timeLimit);
    setIndex((i) => i + 1);
  }, [timeLimit]);

  // The clock. Ticks ten times a second so the ring moves smoothly rather than
  // stepping, and stops the moment an answer lands.
  const timeOutRef = useRef(timeOut);
  timeOutRef.current = timeOut;

  useEffect(() => {
    if (!timeLimit || answered || finished || !question) return undefined;
    const started = Date.now();
    const id = setInterval(() => {
      const left = timeLimit - (Date.now() - started) / 1000;
      if (left <= 0) {
        clearInterval(id);
        setRemaining(0);
        timeOutRef.current();
      } else {
        setRemaining(left);
      }
    }, 100);
    return () => clearInterval(id);
  }, [timeLimit, answered, finished, question, index]);

  // Report the run once, when the last question is left behind.
  useEffect(() => {
    if (finished && results.length) {
      // The third argument is the run in order, which The Daily needs for its
      // share grid. Appended rather than replacing the counts, so every other
      // caller is untouched.
      onFinish?.(
        results.filter((r) => r.correct).length,
        results.length,
        results.map((r) => !!r.correct),
      );
    }
    // Intentionally keyed on `finished` only — re-firing on every result change
    // would award the completion bonus repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  if (questions.length === 0) {
    return (
      <section className="screen">
        <div className="empty">
          <p className="empty__icon">
            <Icon name="flame" size={44} />
          </p>
          <h3>Nothing left to practise here</h3>
          <p>Try another mode — you have answered everything this one had.</p>
          {onExit ? (
            <button type="button" className="btn btn--primary" onClick={onExit}>
              Back to practice
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  if (finished) {
    const correct = results.filter((r) => r.correct).length;
    const perfect = correct === results.length && results.length > 0;

    return (
      <section className="screen">
        <div className="summary">
          <p className="summary__icon">
            <Icon name={perfect ? 'radiance' : 'tablets'} size={44} />
          </p>
          <h3>
            {correct} / {results.length} correct
          </h3>
          {earned > 0 ? <p className="summary__xp">+{earned} XP earned</p> : null}
          <p>
            {perfect
              ? 'A clean sweep. Bonus XP added.'
              : correct >= results.length / 2
                ? 'Solid work — the ones you missed will come round again.'
                : 'Worth another pass. The explanations are the real lesson.'}
          </p>

          {!isPremium && lockedCount > 0 ? (
            <div className="locked-callout">
              <span className="locked-callout__lock">
                <Icon name="lock" size={20} />
              </span>
              <div>
                <strong>{lockedCount} more questions</strong>
                <p>Free covers 5 a session. Premium opens the whole bank.</p>
              </div>
              <button type="button" className="btn btn--primary btn--sm" onClick={onUpgrade}>
                Unlock
              </button>
            </div>
          ) : null}

          <div className="summary__actions">
            {/* Exiting and re-entering builds a fresh set from what is still
                unlearned, which is the point of the mode — so this is the
                primary action, not a replay of the same questions. */}
            {onExit ? (
              <button type="button" className="btn btn--primary" onClick={onExit}>
                Back to practice
              </button>
            ) : null}
            <button
              type="button"
              className={onExit ? 'btn btn--ghost' : 'btn btn--primary'}
              onClick={() => {
                setIndex(0);
                setSelected(null);
                setResults([]);
                setEarned(0);
                setRemaining(timeLimit);
              }}
            >
              Try these again
            </button>
          </div>
        </div>
      </section>
    );
  }

  const progressPct = Math.round((index / questions.length) * 100);

  return (
    <section className="screen">
      <div className="run-bar">
        {onExit ? (
          <button type="button" className="icon-btn icon-btn--sm" onClick={onExit}>
            <Icon name="arrow" size={15} className="icon--flip" />
            Practice
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          className={`sound-btn${soundOn ? ' is-on' : ''}`}
          onClick={onToggleSound}
          aria-pressed={soundOn}
          aria-label={soundOn ? 'Turn answer sounds off' : 'Turn answer sounds on'}
          title={soundOn ? 'Answer sounds on' : 'Answer sounds off'}
        >
          <Icon name={soundOn ? 'soundOn' : 'soundOff'} size={17} />
        </button>
      </div>

      <div className="screen__heading">
        <h2>{title}</h2>
        <p>
          Question {index + 1} of {questions.length} · {question.category} ·{' '}
          <span className={`diff diff--${question.difficulty}`}>{question.difficulty}</span>
          {blurb ? ` · ${blurb}` : ''}
        </p>
      </div>

      <div className="deck-progress">
        <div className="deck-progress__track">
          <div className="deck-progress__fill" style={{ width: `${progressPct}%` }} />
        </div>
        {timeLimit ? (
          <span
            className={`clock${remaining <= timeLimit * 0.3 ? ' is-low' : ''}`}
            aria-label={`${Math.ceil(remaining)} seconds left`}
          >
            <svg viewBox="0 0 36 36" width="30" height="30" aria-hidden="true">
              <circle className="clock__track" cx="18" cy="18" r="15.5" />
              <circle
                className="clock__hand"
                cx="18"
                cy="18"
                r="15.5"
                /* 97.4 is the circumference at r=15.5 — the dash offset is what
                   drains the ring as the seconds go. */
                strokeDasharray="97.4"
                strokeDashoffset={97.4 * (1 - Math.max(0, remaining) / timeLimit)}
              />
            </svg>
            <b>{Math.ceil(Math.max(0, remaining))}</b>
          </span>
        ) : (
          <span className="deck-progress__label">
            +{xpForAnswer(question)} XP
          </span>
        )}
      </div>

      <div className="question">
        <h3 className="question__text">{question.question}</h3>

        <ul className="options">
          {question.options.map((option, i) => {
            const isCorrect = i === question.answer;
            const isChosen = i === selected;
            let className = 'option';
            if (answered && isCorrect) className += ' is-correct';
            else if (answered && isChosen) className += ' is-wrong';
            else if (answered) className += ' is-dim';

            return (
              <li key={option}>
                <button
                  type="button"
                  className={className}
                  onClick={() => choose(i)}
                  disabled={answered}
                >
                  <span className="option__marker" aria-hidden="true">
                    {answered && isCorrect ? (
                      <Icon name="check" size={14} />
                    ) : answered && isChosen ? (
                      <Icon name="close" size={13} />
                    ) : (
                      String.fromCharCode(65 + i)
                    )}
                  </span>
                  <span className="option__label">{option}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {answered ? (
          <div className={`explain${selected === question.answer ? ' is-correct' : ' is-wrong'}`}>
            {/* -1 is the sentinel the clock sets, so a timeout says so rather
                than claiming the player answered wrongly. */}
            <strong>
              {selected === question.answer
                ? 'Correct'
                : selected === -1
                  ? 'Time’s up'
                  : 'Not quite'}
            </strong>
            <p>{question.explanation}</p>
            <button type="button" className="btn btn--primary" onClick={next}>
              {index === questions.length - 1 ? 'See results' : 'Next question'}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
